import { useEffect, useRef, useState, useCallback } from 'react';

export const useFocusManagement = (
    lastFocusedRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>,
    setLastFocusedElement: (element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null) => void,
    updateCurrentInputIndex: (target: HTMLElement) => void
) => {
    const [isFocusedInsideReport, setIsFocusedInsideReport] = useState(false);
    const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const resetFocusState = useCallback(() => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
        setIsFocusedInsideReport(false);
    }, []);

    const handleFocus = useCallback((e: Event) => {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        const reportContainer = document.getElementById('report');

        // Clear any pending blur timeout
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }

        // Check if focus is inside report container
        if (reportContainer && reportContainer.contains(target)) {
            setIsFocusedInsideReport(true);
        }

        // Handle focusable elements
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
            setLastFocusedElement(target);
            updateCurrentInputIndex(target);
        }
    }, [setLastFocusedElement, updateCurrentInputIndex]);

    const handleBlur = useCallback((e: Event) => {
        const target = e.target as HTMLElement;
        const reportContainer = document.getElementById('report');
        const voiceDictationContainer = document.getElementById('report-voice-dictation');

        if (!reportContainer || !reportContainer.contains(target)) return;

        // Check if blur is due to voice dictation interaction
        const relatedTarget = (e as FocusEvent).relatedTarget as HTMLElement;
        if (voiceDictationContainer && relatedTarget && voiceDictationContainer.contains(relatedTarget)) {
            // Restore focus after brief delay
            setTimeout(() => {
                if (lastFocusedRef.current && document.contains(lastFocusedRef.current)) {
                    lastFocusedRef.current.focus();
                }
            }, 10);
            return;
        }

        // Set timeout to update focus state
        blurTimeoutRef.current = setTimeout(() => {
            setIsFocusedInsideReport(false);
            setLastFocusedElement(null);
        }, 120);
    }, [lastFocusedRef, setLastFocusedElement]);

    const handleGlobalClick = useCallback((e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const voiceDictationContainer = document.getElementById('report-voice-dictation');

        // Restore focus when clicking on voice dictation controls
        if (voiceDictationContainer && voiceDictationContainer.contains(target) && lastFocusedRef.current) {
            setTimeout(() => {
                if (lastFocusedRef.current && document.contains(lastFocusedRef.current)) {
                    lastFocusedRef.current.focus();
                }
            }, 50);
        }
    }, [lastFocusedRef]);

    useEffect(() => {
        document.addEventListener('focusin', handleFocus);
        document.addEventListener('focusout', handleBlur);
        document.addEventListener('click', handleGlobalClick);

        return () => {
            document.removeEventListener('focusin', handleFocus);
            document.removeEventListener('focusout', handleBlur);
            document.removeEventListener('click', handleGlobalClick);

            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, [handleFocus, handleBlur, handleGlobalClick]);

    return {
        isFocusedInsideReport,
        resetFocusState
    };
};
